/**
 * §A.1 · Los clubes, desde la sala de máquinas.
 *
 * **La pregunta es «a quién llamo hoy»**, y por eso estado, plan, alumnos y salud
 * van en la misma fila. Un club `at_risk` con noventa alumnos y otro con cuatro no
 * son la misma llamada, y verlo exige tener las dos cifras juntas.
 *
 * Lo que no se reordena: viene por riesgo. Ordenarlo por nombre sería cambiar «a
 * quién llamo» por «cómo se llaman», que es la lista de otro día.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros, fecha } from '@/shared/ui/formato';
import {
  etiquetaDeEstadoDeClub,
  RIESGOS,
  type ClubResumen,
} from '@/backoffice/tipos';

/** Lo que paga al mes, prorrateado si paga por trimestre o por año. */
const mensual = (c: ClubResumen): number =>
  c.price_cents === null
    ? 0
    : c.billing_period === 'annual'
      ? Math.round(c.price_cents / 12)
      : c.billing_period === 'quarterly'
        ? Math.round(c.price_cents / 3)
        : c.price_cents;

type FiltroDeEstado = '' | 'trial' | 'active' | 'suspended' | 'churned';
type FiltroDeRiesgo = '' | 'watch' | 'at_risk' | 'critical';

const ESTADOS: readonly { valor: FiltroDeEstado; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'trial', etiqueta: 'De prueba' },
  { valor: 'active', etiqueta: 'Activos' },
  { valor: 'suspended', etiqueta: 'Suspendidos' },
  { valor: 'churned', etiqueta: 'Se fueron' },
];

const RIESGOS_FILTRO: readonly { valor: FiltroDeRiesgo; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Todo el riesgo' },
  { valor: 'critical', etiqueta: 'Solo críticos' },
  { valor: 'at_risk', etiqueta: 'Solo en riesgo' },
  { valor: 'watch', etiqueta: 'Solo a vigilar' },
];

export function Clubes({ alAbrir }: { alAbrir: (clubId: string) => void }) {
  const [estado, setEstado] = useState<FiltroDeEstado>('');
  const [riesgo, setRiesgo] = useState<FiltroDeRiesgo>('');
  const [busqueda, setBusqueda] = useState('');

  const clubes = useQuery({
    queryKey: ['admin', 'clubes', estado, riesgo, busqueda],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.academies.get({
        query: {
          limit: 200,
          ...(estado ? { status: estado } : {}),
          ...(riesgo ? { risk: riesgo } : {}),
          ...(busqueda.trim() ? { q: busqueda.trim() } : {}),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ClubResumen[];
    },
  });

  /*
   * El club de demostración sale en la lista —hay que poder abrirlo— pero no en
   * los totales: no es un cliente. Hasta el 12-10-2026 sumaba sus 99 € al mes.
   */
  const clientes = (clubes.data ?? []).filter((c) => !c.is_demo);
  const demos = (clubes.data?.length ?? 0) - clientes.length;
  const alumnos = clientes.reduce((s, c) => s + c.student_count, 0);
  /*
   * Lo contratado en esta lista, no el MRR de la plataforma: cambia con los
   * filtros, y eso es lo útil —«los que están en riesgo suman 174 € al mes» es una
   * cifra que decide si merece la pena la tarde de llamadas—. Lo que está aún de
   * prueba se dice aparte: es contratado, pero todavía no entra.
   */
  const alMes = clientes.reduce((s, c) => s + mensual(c), 0);
  const deprueba = clientes
    .filter((c) => c.subscription_status === 'trialing')
    .reduce((s, c) => s + mensual(c), 0);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Clubes</h1>
      </header>

      <div className="filtros">
        <input
          type="search"
          placeholder="Nombre o slug"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <label>
          Estado
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as FiltroDeEstado)}
          >
            {ESTADOS.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label>
          Riesgo
          <select
            value={riesgo}
            onChange={(e) => setRiesgo(e.target.value as FiltroDeRiesgo)}
          >
            {RIESGOS_FILTRO.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {clubes.isPending && <Cargando filas={6} etiqueta="Cargando clubes" />}

      {clubes.isError && (
        <PanelError
          problema={aProblema(clubes.error)}
          reintentar={() => void clubes.refetch()}
        />
      )}

      {clubes.data?.length === 0 && (
        <Vacio
          titulo="Ningún club con esos filtros"
          explicacion="Prueba a quitar el filtro de riesgo: un club recién dado de alta todavía no tiene medida de salud."
        />
      )}

      {clubes.data && clubes.data.length > 0 && (
        <>
          <p className="tenue">
            {cuenta(clientes.length, 'club', 'clubes')},{' '}
            {cuenta(alumnos, 'alumno', 'alumnos')} y{' '}
            <strong>{euros(alMes)} al mes</strong> contratados
            {deprueba > 0 && (
              <>, de los que {euros(deprueba)} son de clubes aún de prueba</>
            )}
            .{demos > 0 && ' Sin contar el club de demostración.'} El importe es
            de esta lista, así que cambia con los filtros.
          </p>

          {/* En el móvil, una tarjeta por club (`tabla-tarjetas`, tema.css). */}
          <div className="tabla-marco">
            <table className="tabla tabla-tarjetas">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Estado</th>
                  <th>Plan</th>
                  <th className="num">Al mes</th>
                  <th className="num">Alumnos</th>
                  <th>Salud</th>
                  <th>Alta</th>
                </tr>
              </thead>
              {/* Sin reordenar: viene por riesgo, que es la decisión. */}
              <tbody>
                {clubes.data.map((c) => {
                  const r = c.risk_level ? RIESGOS[c.risk_level] : undefined;
                  return (
                    <tr
                      key={c.id}
                      className={
                        c.risk_level === 'critical' ? 'fila-aviso' : undefined
                      }
                    >
                      <td>
                        <button
                          type="button"
                          className="enlace-fila"
                          onClick={() => alAbrir(c.id)}
                        >
                          {c.trade_name}
                        </button>
                        {c.is_demo && (
                          <>
                            {' '}
                            <span
                              className="pastilla"
                              title="Club de demostración: no es un cliente"
                            >
                              Demo
                            </span>
                          </>
                        )}
                        <div className="tenue">
                          <code>{c.slug}</code>
                        </div>
                      </td>
                      <td data-label="Estado">
                        <span
                          className={`pastilla ${
                            c.status === 'active'
                              ? 'pastilla-bien'
                              : c.status === 'churned'
                                ? 'pastilla-mal'
                                : 'pastilla-info'
                          }`}
                        >
                          {etiquetaDeEstadoDeClub(c.status)}
                        </span>
                      </td>
                      <td className="tenue" data-label="Plan">
                        {c.plan_name ?? '—'}
                      </td>
                      <td className="num" data-label="Al mes">
                        {c.price_cents === null ? (
                          '—'
                        ) : (
                          <>
                            {euros(c.price_cents)}
                            {c.billing_period === 'annual' && (
                              <span className="tenue"> /año</span>
                            )}
                            {c.billing_period === 'quarterly' && (
                              <span className="tenue"> /trimestre</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="num" data-label="Alumnos">
                        {c.student_count}
                      </td>
                      <td data-label="Salud">
                        {/*
                          El número y la palabra juntos: «73» no es una decisión y
                          «En riesgo» sin cifra no deja comparar dos clubes.
                        */}
                        {r ? (
                          <>
                            <span className={`pastilla ${r.clase}`}>
                              {r.etiqueta}
                            </span>
                            {c.health_score !== null && (
                              <span className="tenue"> {c.health_score}</span>
                            )}
                          </>
                        ) : (
                          <span className="tenue">Sin medir</span>
                        )}
                      </td>
                      <td className="tenue" data-label="Alta">
                        {fecha(c.onboarded_at ?? c.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
